import React, { useState, useEffect, useCallback } from 'react';
import { TodoItem, CreateTodoRequest } from '../types/todo';

interface TodoListProps {
  userEmail: string;
  propertyUrl: string;
  refreshTrigger?: number;
}

const PriorityBadge: React.FC<{ priority: 'high' | 'medium' | 'low' }> = ({ priority }) => {
  const styles = {
    high: 'bg-red-100 text-red-700 border-red-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-gray-100 text-gray-600 border-gray-200'
  };
  const labels = { high: 'High', medium: 'Med', low: 'Low' };
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${styles[priority]}`}>
      {labels[priority]}
    </span>
  );
};

const StatusBadge: React.FC<{
  status: 'pending' | 'in_progress' | 'completed';
  onStatusChange: (newStatus: 'pending' | 'in_progress' | 'completed') => void;
}> = ({ status, onStatusChange }) => {
  const styles = {
    pending: 'bg-blue-100 text-blue-700 border-blue-200 cursor-pointer hover:bg-blue-200',
    in_progress: 'bg-orange-100 text-orange-700 border-orange-200 cursor-pointer hover:bg-orange-200',
    completed: 'bg-green-100 text-green-700 border-green-200 cursor-pointer hover:bg-green-200'
  };
  const labels = { pending: 'To Do', in_progress: 'In Progress', completed: 'Done' };
  const getNextStatus = (): 'pending' | 'in_progress' | 'completed' => {
    switch (status) {
      case 'pending': return 'in_progress';
      case 'in_progress': return 'completed';
      case 'completed': return 'pending';
      default: return 'pending';
    }
  };
  return (
    <button
      onClick={() => onStatusChange(getNextStatus())}
      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border transition-colors ${styles[status]}`}
      title={`Click to change status (currently: ${labels[status]})`}
    >
      {labels[status]}
    </button>
  );
};

const bgColors = {
  pending: 'bg-blue-50 border-blue-100',
  in_progress: 'bg-orange-50 border-orange-100',
  completed: 'bg-green-50 border-green-100 opacity-75'
};

interface TodoCardProps {
  todo: TodoItem;
  onStatusChange: (id: number, status: 'pending' | 'in_progress' | 'completed') => void;
  onUpdateNotes: (id: number, notes: string) => void;
  onUpdateAssignment: (id: number, assignedTo: string | null) => void;
  onDelete: (id: number) => void;
}

const TodoCard: React.FC<TodoCardProps> = ({ todo, onStatusChange, onUpdateNotes, onUpdateAssignment, onDelete }) => {
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState(todo.notes || '');
  const [assigningMode, setAssigningMode] = useState(false);
  const [assignText, setAssignText] = useState(todo.assigned_to || '');

  const handleSaveNotes = () => {
    onUpdateNotes(todo.id, notesValue);
    setEditingNotes(false);
  };

  const handleSaveAssign = () => {
    const trimmed = assignText.trim();
    onUpdateAssignment(todo.id, trimmed || null);
    setAssigningMode(false);
  };

  const handleClearAssign = () => {
    onUpdateAssignment(todo.id, null);
    setAssignText('');
  };

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${bgColors[todo.status]}`}>
      <StatusBadge
        status={todo.status}
        onStatusChange={(newStatus) => onStatusChange(todo.id, newStatus)}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <PriorityBadge priority={todo.priority} />
          {todo.source_panel && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {todo.source_panel}
            </span>
          )}
        </div>
        <h4 className={`text-sm font-medium text-gray-900 ${todo.status === 'completed' ? 'line-through' : ''}`}>
          {todo.title}
        </h4>
        {todo.description && (
          <p className="text-xs text-gray-600 mt-1">{todo.description}</p>
        )}

        {editingNotes ? (
          <div className="mt-2 space-y-2">
            <textarea
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              placeholder="Add notes..."
              className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
              rows={2}
              autoFocus
            />
            <div className="flex gap-1">
              <button
                onClick={handleSaveNotes}
                className="text-xs bg-gradient-to-r from-orange-400 to-pink-600 hover:from-orange-500 hover:to-pink-700 text-white px-2 py-1 rounded"
              >
                Save
              </button>
              <button
                onClick={() => { setEditingNotes(false); setNotesValue(todo.notes || ''); }}
                className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-1">
            {todo.notes ? (
              <div className="flex items-start gap-2">
                <p className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded flex-1">
                  📝 {todo.notes}
                </p>
                <button
                  onClick={() => { setNotesValue(todo.notes || ''); setEditingNotes(true); }}
                  className="text-xs text-gray-400 hover:text-blue-600"
                  title="Edit notes"
                >
                  ✏️
                </button>
              </div>
            ) : (
              <button
                onClick={() => { setNotesValue(''); setEditingNotes(true); }}
                className="text-xs text-gray-400 hover:text-blue-600 mt-1"
              >
                + Add notes
              </button>
            )}
          </div>
        )}

        {assigningMode ? (
          <div className="mt-2 flex items-center gap-1">
            <input
              type="text"
              value={assignText}
              onChange={(e) => setAssignText(e.target.value)}
              placeholder="Enter name to assign..."
              className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveAssign(); if (e.key === 'Escape') setAssigningMode(false); }}
            />
            <button
              onClick={handleSaveAssign}
              className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded"
            >
              Save
            </button>
            <button
              onClick={() => setAssigningMode(false)}
              className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            {todo.assigned_to ? (
              <span className="text-xs text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                👤 {todo.assigned_to}
                <button
                  onClick={handleClearAssign}
                  className="text-purple-400 hover:text-purple-700 ml-1 leading-none"
                  title="Clear assignment"
                >
                  ✕
                </button>
              </span>
            ) : null}
            <button
              onClick={() => { setAssignText(todo.assigned_to || ''); setAssigningMode(true); }}
              className="text-xs text-purple-500 hover:text-purple-700"
            >
              {todo.assigned_to ? 'Reassign' : '+ Assign'}
            </button>
          </div>
        )}

        <p className="text-xs text-gray-400 mt-1">
          Added {new Date(todo.created_at).toLocaleDateString()} by {todo.user_email}
        </p>
      </div>
      <button
        onClick={() => onDelete(todo.id)}
        className="text-gray-400 hover:text-red-500 text-xs p-1 flex-shrink-0"
        title="Delete this to-do item"
      >
        ✕
      </button>
    </div>
  );
};

const TodoList: React.FC<TodoListProps> = ({ userEmail, propertyUrl, refreshTrigger }) => {
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTodoTitle, setNewTodoTitle] = useState('');
  const [newTodoNotes, setNewTodoNotes] = useState('');
  const [newTodoPriority, setNewTodoPriority] = useState<'high' | 'medium' | 'low'>('medium');

  const fetchTodos = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/todos?propertyUrl=${encodeURIComponent(propertyUrl)}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setTodos(data);
    } catch (err) {
      setError('Failed to load to-do items');
    } finally {
      setLoading(false);
    }
  }, [propertyUrl]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos, refreshTrigger]);

  const updateTodoStatus = async (id: number, newStatus: 'pending' | 'in_progress' | 'completed') => {
    try {
      const response = await fetch(`/api/todos/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const updatedTodo = await response.json();
      setTodos(prev => prev.map(t => t.id === id ? updatedTodo : t));
    } catch (err) {
      console.error('Error updating todo status:', err);
      alert('Failed to update to-do status');
    }
  };

  const updateTodoNotes = async (id: number, notes: string) => {
    try {
      const response = await fetch(`/api/todos/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notes || null }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const updatedTodo = await response.json();
      setTodos(prev => prev.map(t => t.id === id ? updatedTodo : t));
    } catch (err) {
      console.error('Error updating todo notes:', err);
      alert('Failed to update notes');
    }
  };

  const updateTodoAssignment = async (id: number, assignedTo: string | null) => {
    try {
      const response = await fetch(`/api/todos/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_to: assignedTo }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const updatedTodo = await response.json();
      setTodos(prev => prev.map(t => t.id === id ? updatedTodo : t));
    } catch (err) {
      console.error('Error updating assignment:', err);
      alert('Failed to update assignment');
    }
  };

  const deleteTodo = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this to-do item?')) return;
    try {
      const response = await fetch(`/api/todos/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      setTodos(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.error('Error deleting todo:', err);
      alert('Failed to delete to-do item');
    }
  };

  const addTodo = useCallback(async (todoData: Omit<CreateTodoRequest, 'userEmail' | 'propertyUrl'>) => {
    try {
      const response = await fetch('/api/todos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, propertyUrl, ...todoData }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const newTodo = await response.json();
      setTodos(prev => [newTodo, ...prev]);
      return newTodo;
    } catch (err) {
      throw err;
    }
  }, [userEmail, propertyUrl]);

  const pendingTodos = todos.filter(t => t.status === 'pending');
  const inProgressTodos = todos.filter(t => t.status === 'in_progress');
  const completedTodos = todos.filter(t => t.status === 'completed');

  const handleManualAdd = async () => {
    if (!newTodoTitle.trim()) return;
    try {
      await addTodo({ title: newTodoTitle.trim(), notes: newTodoNotes.trim() || undefined, priority: newTodoPriority, sourcePanel: 'Manual' });
      setNewTodoTitle('');
      setNewTodoNotes('');
      setNewTodoPriority('medium');
      setShowAddForm(false);
    } catch (err) {
      alert('Failed to add to-do item');
    }
  };

  const AddForm = () => (
    <div className="space-y-3 mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
      <input
        type="text"
        value={newTodoTitle}
        onChange={(e) => setNewTodoTitle(e.target.value)}
        placeholder="Enter task description..."
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        autoFocus
      />
      <textarea
        value={newTodoNotes}
        onChange={(e) => setNewTodoNotes(e.target.value)}
        placeholder="Add notes (optional)..."
        rows={2}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
      />
      <div className="flex items-center justify-between">
        <select
          value={newTodoPriority}
          onChange={(e) => setNewTodoPriority(e.target.value as 'high' | 'medium' | 'low')}
          className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="low">Low Priority</option>
          <option value="medium">Medium Priority</option>
          <option value="high">High Priority</option>
        </select>
        <div className="flex gap-2">
          <button onClick={() => setShowAddForm(false)} className="px-3 py-1 text-gray-600 hover:text-gray-800 text-sm">
            Cancel
          </button>
          <button
            onClick={handleManualAdd}
            disabled={!newTodoTitle.trim()}
            className="px-3 py-1 bg-gradient-to-r from-orange-400 to-pink-600 hover:from-orange-500 hover:to-pink-700 disabled:bg-gray-400 text-white text-sm rounded transition-colors"
          >
            Add Task
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
            <div className="h-3 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-red-200 shadow-sm p-6">
        <div className="text-red-600 text-sm">{error}</div>
        <button onClick={fetchTodos} className="mt-2 text-xs bg-red-100 text-red-600 px-3 py-1 rounded hover:bg-red-200 transition-colors">
          Retry
        </button>
      </div>
    );
  }

  if (todos.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">📋 To-Do List</h3>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="text-sm bg-gradient-to-r from-orange-400 to-pink-600 hover:from-orange-500 hover:to-pink-700 text-white px-3 py-1 rounded transition-colors"
          >
            + Add Task
          </button>
        </div>
        {showAddForm ? <AddForm /> : (
          <p className="text-gray-500 text-sm">
            No action items yet. Use "Add to to-do list" buttons throughout the dashboard or the button above to track SEO tasks.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          📋 To-Do List
          <span className="text-sm font-normal text-gray-500">({todos.length} items)</span>
        </h3>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-4 text-xs">
            <span className="text-blue-600">{pendingTodos.length} To Do</span>
            <span className="text-orange-600">{inProgressTodos.length} In Progress</span>
            <span className="text-green-600">{completedTodos.length} Done</span>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="text-sm bg-gradient-to-r from-orange-400 to-pink-600 hover:from-orange-500 hover:to-pink-700 text-white px-3 py-1 rounded transition-colors"
          >
            + Add Task
          </button>
        </div>
      </div>

      {showAddForm && <AddForm />}

      <div className="space-y-3">
        {[...pendingTodos, ...inProgressTodos, ...completedTodos].map(todo => (
          <TodoCard
            key={todo.id}
            todo={todo}
            onStatusChange={updateTodoStatus}
            onUpdateNotes={updateTodoNotes}
            onUpdateAssignment={updateTodoAssignment}
            onDelete={deleteTodo}
          />
        ))}
      </div>
    </div>
  );
};

export default TodoList;
